import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import { PLATFORM_SCOPE_LABELS, type PlatformBien, type PlatformBienInput } from "@/store/platform/types";
import {
  BIEN_STATUT_OPTIONS,
  BIEN_TYPE_OPTIONS,
  type BienStatut,
  type BienType,
} from "@/pages/proprietaire/biens/types";

interface PlatformBienFormProps {
  open: boolean;
  onClose: () => void;
  bien: PlatformBien | null;
}

interface FormState {
  immeubleId: string;
  type: BienType | "";
  reference: string;
  etage: string;
  numero: string;
  surface: string;
  loyerMensuel: string;
  statut: BienStatut;
  description: string;
  infosComplementaires: string;
}

const EMPTY_STATE: FormState = {
  immeubleId: "",
  type: "",
  reference: "",
  etage: "",
  numero: "",
  surface: "",
  loyerMensuel: "",
  statut: "disponible",
  description: "",
  infosComplementaires: "",
};

/**
 * Édition d'un bien depuis le Super Admin (la création reste dans les espaces
 * Propriétaire / Agence). Le rattachement immeuble → propriétaire est maintenu.
 */
export default function PlatformBienForm({ open, onClose, bien }: PlatformBienFormProps) {
  const { immeubles, updateBien } = usePlatformData();
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || !bien) return;
    setForm({
      immeubleId: bien.immeubleId,
      type: bien.type as BienType,
      reference: bien.reference,
      etage: bien.etage,
      numero: bien.numero,
      surface: bien.surface ? String(bien.surface) : "",
      loyerMensuel: String(bien.loyerMensuel),
      statut: bien.statut as BienStatut,
      description: bien.description,
      infosComplementaires: bien.infosComplementaires,
    });
    setErrors({});
  }, [open, bien]);

  const immeubleOptions = useMemo(
    () =>
      immeubles.map((immeuble) => ({
        value: immeuble.id,
        label: `${immeuble.nom} — ${PLATFORM_SCOPE_LABELS[immeuble.scope]}`,
      })),
    [immeubles]
  );

  if (!bien) return null;

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    const nextErrors: Record<string, string> = {};
    if (!form.immeubleId) nextErrors.immeubleId = "Sélectionnez l'immeuble du bien.";
    if (!form.type) nextErrors.type = "Sélectionnez un type de bien.";
    if (!form.loyerMensuel.trim() || Number(form.loyerMensuel) <= 0) {
      nextErrors.loyerMensuel = "Indiquez un loyer mensuel valide.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const immeuble = immeubles.find((item) => item.id === form.immeubleId);
    const input: PlatformBienInput = {
      scope: immeuble ? immeuble.scope : bien.scope,
      proprietaireId: immeuble ? immeuble.proprietaireId : bien.proprietaireId,
      immeubleId: form.immeubleId,
      type: form.type as BienType,
      reference: form.reference.trim(),
      etage: form.etage.trim(),
      numero: form.numero.trim(),
      surface: Math.max(0, Number(form.surface) || 0),
      loyerMensuel: Math.max(0, Number(form.loyerMensuel) || 0),
      statut: form.statut,
      description: form.description.trim(),
      infosComplementaires: form.infosComplementaires.trim(),
    };
    updateBien(bien.id, input);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Modifier le bien"
      description="Corrigez les informations de ce bien. L'immeuble détermine automatiquement l'espace et le propriétaire."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-save-line text-base" aria-hidden="true" />
            Enregistrer
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
        <Field label="Immeuble" htmlFor="sa-bien-immeuble" required error={errors.immeubleId} className="sm:col-span-2">
          <Select
            id="sa-bien-immeuble"
            value={form.immeubleId}
            onChange={(event) => update("immeubleId", event.target.value)}
            options={immeubleOptions}
            placeholder={immeubleOptions.length > 0 ? "Sélectionnez un immeuble" : "Aucun immeuble"}
            disabled={immeubleOptions.length === 0}
            invalid={Boolean(errors.immeubleId)}
          />
        </Field>

        <Field label="Type" htmlFor="sa-bien-type" required error={errors.type}>
          <Select
            id="sa-bien-type"
            value={form.type}
            onChange={(event) => update("type", event.target.value)}
            options={BIEN_TYPE_OPTIONS}
            placeholder="Sélectionnez un type"
            invalid={Boolean(errors.type)}
          />
        </Field>

        <Field label="Référence" htmlFor="sa-bien-reference">
          <Input
            id="sa-bien-reference"
            value={form.reference}
            onChange={(event) => update("reference", event.target.value)}
            maxLength={60}
          />
        </Field>

        <Field label="Étage" htmlFor="sa-bien-etage">
          <Input
            id="sa-bien-etage"
            value={form.etage}
            onChange={(event) => update("etage", event.target.value)}
            maxLength={40}
          />
        </Field>

        <Field label="Numéro du bien" htmlFor="sa-bien-numero">
          <Input
            id="sa-bien-numero"
            value={form.numero}
            onChange={(event) => update("numero", event.target.value)}
            maxLength={40}
          />
        </Field>

        <Field label="Surface (m²)" htmlFor="sa-bien-surface">
          <Input
            id="sa-bien-surface"
            type="number"
            min={0}
            value={form.surface}
            onChange={(event) => update("surface", event.target.value)}
          />
        </Field>

        <Field label="Loyer mensuel (FCFA)" htmlFor="sa-bien-loyer" required error={errors.loyerMensuel}>
          <Input
            id="sa-bien-loyer"
            type="number"
            min={0}
            value={form.loyerMensuel}
            onChange={(event) => update("loyerMensuel", event.target.value)}
            invalid={Boolean(errors.loyerMensuel)}
          />
        </Field>

        <Field label="Statut" htmlFor="sa-bien-statut">
          <Select
            id="sa-bien-statut"
            value={form.statut}
            onChange={(event) => update("statut", event.target.value)}
            options={BIEN_STATUT_OPTIONS}
          />
        </Field>

        <Field label="Description" htmlFor="sa-bien-description" className="sm:col-span-2">
          <Textarea
            id="sa-bien-description"
            value={form.description}
            onChange={(event) => update("description", event.target.value)}
            className="min-h-[88px]"
            maxLength={600}
          />
        </Field>

        <Field label="Informations complémentaires" htmlFor="sa-bien-infos" className="sm:col-span-2">
          <Textarea
            id="sa-bien-infos"
            value={form.infosComplementaires}
            onChange={(event) => update("infosComplementaires", event.target.value)}
            className="min-h-[72px]"
            maxLength={600}
          />
        </Field>
      </div>
    </Modal>
  );
}